import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Fullscreen photo lightbox with keyboard navigation + previous/next.
 * - `photos` : array of URLs
 * - `initialIndex` : photo to open first
 * - `onClose` : callback when user closes the lightbox
 */
export default function PhotoLightbox({ photos, initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex);

  const close = useCallback(() => onClose && onClose(), [onClose]);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + photos.length) % photos.length),
    [photos.length]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % photos.length),
    [photos.length]
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', handleKey);
    // Lock page scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [close, prev, next]);

  if (!photos || photos.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center animate-in fade-in"
      onClick={close}
      data-testid="photo-lightbox"
    >
      {/* close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); close(); }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        aria-label="Fermer"
        data-testid="lightbox-close"
      >
        <X size={22} />
      </button>

      {/* counter */}
      {photos.length > 1 && (
        <span className="absolute top-6 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium select-none">
          {index + 1} / {photos.length}
        </span>
      )}

      {/* prev */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-5 md:left-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          aria-label="Précédent"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* image */}
      <img
        src={photos[index]}
        alt={`Photo ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl select-none"
      />

      {/* next */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-5 md:right-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          aria-label="Suivant"
          data-testid="lightbox-next"
        >
          <ChevronRight size={24} />
        </button>
      )}
    </div>
  );
}

/**
 * Thumbnail strip — click opens the lightbox on that photo.
 */
export function ReviewPhotos({ photos, maxThumbs = 4, size = 'md' }) {
  const [open, setOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  if (!photos || photos.length === 0) return null;

  const sizes = {
    sm: 'w-14 h-14',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
  };
  const thumbClass = sizes[size] || sizes.md;

  const displayed = photos.slice(0, maxThumbs);
  const remaining = photos.length - displayed.length;

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="review-photos">
        {displayed.map((src, i) => (
          <button
            type="button"
            key={i}
            onClick={() => { setStartIndex(i); setOpen(true); }}
            className={`${thumbClass} rounded-xl overflow-hidden bg-gray-100 ring-1 ring-gray-200 hover:ring-2 hover:ring-[#00aaff] hover:-translate-y-0.5 transition-all duration-200 cursor-zoom-in relative group`}
            data-testid={`review-photo-thumb-${i}`}
          >
            <img src={src} alt={`Avis photo ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            {i === displayed.length - 1 && remaining > 0 && (
              <span className="absolute inset-0 bg-black/55 text-white font-bold text-sm flex items-center justify-center">
                +{remaining}
              </span>
            )}
          </button>
        ))}
      </div>
      {open && (
        <PhotoLightbox
          photos={photos}
          initialIndex={startIndex}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
